// Shared array mechanics for "multi-row create" forms (add/remove/update rows).
// Row shape/validation/payload-building stays in each feature's own module.

export type FormRow = { rowKey: string };

// Monotonic per-kind counter (not Date.now()/Math.random()) so rowKeys stay
// unique and deterministic without cross-feature collisions.
const rowSequenceByKind = new Map<string, number>();

export const createRowKey = (kind: string): string => {
  const next = (rowSequenceByKind.get(kind) ?? 0) + 1;
  rowSequenceByKind.set(kind, next);
  return `${kind}-${next}`;
};

export const addFormRow = <T extends FormRow>(rows: T[], newRow: T): T[] => [...rows, newRow];

// No-ops when index is 0 (first row is never removable — removing the only
// row would leave nothing to create) or out of bounds.
export const removeFormRow = <T extends FormRow>(rows: T[], index: number): T[] => {
  if (index <= 0 || index >= rows.length) return rows;
  return rows.filter((_, rowIndex) => rowIndex !== index);
};

export const updateFormRowField = <T extends FormRow, K extends keyof T>(
  rows: T[],
  index: number,
  field: K,
  value: T[K],
): T[] => rows.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row));
